// Translates feedback blocks to and from the survey structure the rest of
// KAPsurvey already understands.
//
// The rule that keeps this simple: a feedback survey is an ORDINARY survey made
// of ordinary questions. Every block maps onto an existing `InputType` using
// only fields the survey builder API already writes — no new backend field is
// required — so the generic preview, the form builder, publishing, submissions
// and exports all work on the result untouched.
//
// Stars and the 0–10 recommendation would ideally be RANGE_QUESTION with
// `appearance: "rating"`, which the submission type already renders as antd
// `Rate`. They were built as SELECT_ONE with emoji choices while `appearance`
// could not be written (the old `Survey/EditSurvey` payload never sent it); that
// round-trips perfectly and still reads correctly in the generic preview.

import { v4 as uuidv4 } from "uuid";
import { InputType } from "../../../../utils/enums";
import {
   BlockType,
   CONTACT_FIELDS,
   DEFAULT_FOLLOW_UP_PROMPT,
   OTHER_LABEL,
   OTHER_OPTION_ID,
   OTHER_SPECIFY_LABEL,
   SMILEY_SETS,
   THUMB_SETS,
   followUpKey,
   followUpOf,
   followUpReasonsOf,
   followUpStyleOf,
   getLikertLabels,
   getSmileyPoints,
   getThumbIcons,
   getThumbText,
   inShownOrder,
   isLowScore,
   makeOption,
   otherEnabled,
   otherKey,
   otherSelected,
   pointKeysOf,
   selectableOptions,
   smileyLabelsFrom,
   smileyPointsOf
} from "./blocks";

const STAR_EMOJI = "⭐";

/** Question types this builder can represent. Anything else must not be touched. */
export const SUPPORTED_QUESTION_TYPES = [
   InputType.SELECT_ONE,
   InputType.SELECT_MULTIPLE,
   InputType.TEXT,
   InputType.NUMBER,
   InputType.DATE,
   InputType.PHONE_NUMBER
];

const starLabel = (value) => `${value} star${value === 1 ? "" : "s"}`;

/**
 * The contact details a CONTACT block asks for, in catalog order.
 *
 * This is the one place that decides how many questions such a block becomes —
 * writing them and reading answers back off it both go through here, so they
 * cannot disagree about the count or the order.
 */
const contactFieldsOf = (block) =>
   CONTACT_FIELDS.filter((field) => block.config?.fields?.includes(field.key));

/**
 * A choice in the shape the editor produces. An id starting with "dummy" marks a
 * choice as new; the save mints its real id (`builderApi/diffSurvey`).
 */
const choice = (text, order, emoji) => ({
   id: `dummy${uuidv4()}`,
   order,
   ...(emoji ? { emoji } : {}),
   choiceLabels: [{ text }]
});

/** A question in the shape the editor produces, flagged as newly created. */
const questionItem = ({
   questionType,
   label,
   description = null,
   isRequired,
   choices = [],
   hasEmoji = false,
   order
}) => {
   const id = uuidv4();

   return {
      itemLabel: "Question",
      id,
      surveyItem: {
         id,
         title: "",
         name: "",
         // Never written: the sub-text lives in the hint alone (below).
         description: null,
         questionType,
         order,
         created: true,
         isRequired: Boolean(isRequired),
         hasEmoji,
         maxChoices: null,
         constraint: null,
         calculation: null,
         default: null,
         questionLabels: [{ text: label }],
         // The sub-text is the question's hint: where KAPsurvey keeps it — the
         // form builder edits it there, the generic preview draws it,
         // `AddTranslation` takes it — and the one sub-text field the
         // per-object API can write (it has no `description`).
         surveyQuestionHints: description ? [{ text: description }] : [],
         selectQuestionChoices: choices,
         range: null,
         secondChoices: []
      }
   };
};

/**
 * Appends the low-score follow-up as its own question when the block asks for
 * one — a TEXT comment box, or a SELECT_MULTIPLE when the author chose tappable
 * reasons. It is never required — it only ever appears after a low rating, so a
 * respondent who rated well must be able to pass it untouched.
 */
const withFollowUp = (block, questions, nextOrder) => {
   const followUp = followUpOf(block);
   if (!followUp) return questions;

   const label = followUp.prompt?.trim() || DEFAULT_FOLLOW_UP_PROMPT;
   const order = nextOrder + questions.length;

   if (followUpStyleOf(followUp) === "reasons") {
      return [
         ...questions,
         questionItem({
            label,
            isRequired: false,
            order,
            questionType: InputType.SELECT_MULTIPLE,
            choices: followUpReasonsOf(followUp).map((reason, index) =>
               choice(reason.text, index + 1)
            )
         })
      ];
   }

   return [
      ...questions,
      questionItem({ label, isRequired: false, order, questionType: InputType.TEXT })
   ];
};

/** One feedback block becomes one or more survey questions. */
const blockToQuestions = (block, nextOrder) => {
   const label = block.label?.trim() || "Untitled question";
   const base = {
      label,
      description: block.description?.trim() || null,
      isRequired: block.required
   };

   switch (block.type) {
      case BlockType.SMILEY: {
         // Happiest first, the order the kiosk draws them in (see
         // `chosenOrders` for how an answer finds its face).
         const points = inShownOrder(block.type, smileyPointsOf(block));
         return withFollowUp(
            block,
            [
               questionItem({
                  ...base,
                  order: nextOrder,
                  questionType: InputType.SELECT_ONE,
                  hasEmoji: true,
                  choices: points.map((point, index) => choice(point.text, index + 1, point.emoji))
               })
            ],
            nextOrder
         );
      }

      case BlockType.STARS: {
         const max = block.config.max ?? 5;
         return withFollowUp(
            block,
            [
               questionItem({
                  ...base,
                  order: nextOrder,
                  questionType: InputType.SELECT_ONE,
                  hasEmoji: true,
                  choices: Array.from({ length: max }, (_, index) =>
                     choice(starLabel(index + 1), index + 1, STAR_EMOJI)
                  )
               })
            ],
            nextOrder
         );
      }

      case BlockType.MULTISTARS:
         // Like CONTACT, one block fans out into one question per row — here a
         // 5-star question per aspect, labelled with the aspect itself. The
         // block's description rides on the first row, the only question that
         // can carry it.
         return (block.options ?? []).map((aspect, index) =>
            questionItem({
               label: aspect.text?.trim() || `Aspect ${index + 1}`,
               description: index === 0 ? base.description : null,
               isRequired: block.required,
               order: nextOrder + index,
               questionType: InputType.SELECT_ONE,
               hasEmoji: true,
               choices: Array.from({ length: 5 }, (_, star) =>
                  choice(starLabel(star + 1), star + 1, STAR_EMOJI)
               )
            })
         );

      case BlockType.NPS:
         return withFollowUp(
            block,
            [
               questionItem({
                  ...base,
                  order: nextOrder,
                  questionType: InputType.SELECT_ONE,
                  choices: Array.from({ length: 11 }, (_, score) =>
                     choice(String(score), score + 1)
                  )
               })
            ],
            nextOrder
         );

      case BlockType.SCALE: {
         const max = block.config.max ?? 5;
         return withFollowUp(
            block,
            [
               questionItem({
                  ...base,
                  order: nextOrder,
                  questionType: InputType.SELECT_ONE,
                  choices: Array.from({ length: max }, (_, index) =>
                     choice(String(index + 1), index + 1)
                  )
               })
            ],
            nextOrder
         );
      }

      case BlockType.LIKERT:
         return withFollowUp(
            block,
            [
               questionItem({
                  ...base,
                  order: nextOrder,
                  questionType: InputType.SELECT_ONE,
                  choices: inShownOrder(
                     block.type,
                     getLikertLabels(block.config.scale)
                  ).map((text, index) => choice(text, index + 1))
               })
            ],
            nextOrder
         );

      case BlockType.THUMBS: {
         const icons = getThumbIcons(block.config.iconSet);
         const text = getThumbText(block);
         return withFollowUp(
            block,
            [
               questionItem({
                  ...base,
                  order: nextOrder,
                  questionType: InputType.SELECT_ONE,
                  hasEmoji: true,
                  choices: [
                     // Blank wording falls back rather than writing an empty
                     // choice, which the save would silently drop.
                     choice(text.up, 1, icons.up),
                     choice(text.down, 2, icons.down)
                  ]
               })
            ],
            nextOrder
         );
      }

      case BlockType.TAGS:
      case BlockType.CHOICE: {
         const options = selectableOptions(block);
         const choices = options.map((item, index) => choice(item.text, index + 1));
         if (otherEnabled(block)) {
            choices.push(choice(OTHER_LABEL, choices.length + 1));
         }

         const questions = [
            questionItem({
               ...base,
               order: nextOrder,
               questionType:
                  block.type === BlockType.TAGS
                     ? InputType.SELECT_MULTIPLE
                     : InputType.SELECT_ONE,
               choices
            })
         ];

         // The "Other…" write-in rides directly behind its block's own
         // question, exactly like a low-score follow-up does.
         if (otherEnabled(block)) {
            questions.push(
               questionItem({
                  label: OTHER_SPECIFY_LABEL,
                  isRequired: false,
                  order: nextOrder + 1,
                  questionType: InputType.TEXT
               })
            );
         }

         return questions;
      }

      case BlockType.COMMENT:
      case BlockType.SHORT_TEXT:
         return [questionItem({ ...base, order: nextOrder, questionType: InputType.TEXT })];

      case BlockType.NUMBER:
         return [questionItem({ ...base, order: nextOrder, questionType: InputType.NUMBER })];

      case BlockType.DATE:
         return [questionItem({ ...base, order: nextOrder, questionType: InputType.DATE })];

      case BlockType.CONTACT:
         // Each requested detail becomes its own question, labelled with the
         // field name so it reads correctly in the generic preview too. As with
         // MULTISTARS, the block's description rides on the first one.
         return contactFieldsOf(block).map((field, index) =>
            questionItem({
               label: field.label,
               description: index === 0 ? base.description : null,
               isRequired: false,
               order: nextOrder + index,
               questionType: field.key === "phone" ? InputType.PHONE_NUMBER : InputType.TEXT
            })
         );

      default:
         return [];
   }
};

/**
 * Puts the ids a question was last saved under back onto the freshly written
 * item, so the save finds it on the survey and UPDATES that question instead of
 * creating another (`builderApi/diffSurvey` matches by id, on the outer item, so
 * both carry it). That is what keeps answers already filed against it attached,
 * and so what lets a survey with submissions be edited here at all.
 *
 * Choices are matched by position, which is their identity here: every block
 * writes its choices in one fixed order (see `blockToQuestions`), so the nth is
 * the same point on the scale, the same option row, as the nth one saved. Ones
 * past the end — a scale the author lengthened, an option they added — keep
 * their "dummy" id and are created. Reordering a block's options therefore
 * moves the wording onto the neighbouring choice rather than swapping ids, the
 * same as retyping both of them in the standard form builder would.
 *
 * Except on a smiley or agree/disagree question, where `pointKeys` says which
 * point each written choice is and the saved one is found by that instead
 * (`pointKeysOf`). Changing such a scale between 3 and 5 points adds or removes
 * points in the MIDDLE, so by position the 3-point "Average" would become the
 * 5-point "Liked it" — and every answer already filed against it with it. By
 * point, the faces both scales share keep their ids, a new face is created and
 * a dropped one is deleted (which the server refuses once it has answers).
 */
const graftSavedIds = (item, saved, sectionId, pointKeys = null) => {
   if (!saved?.id) return item;

   const idByPoint =
      pointKeys && saved.pointKeys?.length === saved.choiceIds?.length
         ? new Map(saved.pointKeys.map((key, index) => [key, saved.choiceIds[index]]))
         : null;

   return {
      ...item,
      id: saved.id,
      surveyItem: {
         ...item.surveyItem,
         id: saved.id,
         created: false,
         sectionId: saved.sectionId ?? sectionId,
         selectQuestionChoices: (item.surveyItem.selectQuestionChoices ?? []).map(
            (choiceItem, index) => {
               const choiceId = idByPoint
                  ? idByPoint.get(pointKeys[index])
                  : saved.choiceIds?.[index];
               return choiceId ? { ...choiceItem, id: choiceId } : choiceItem;
            }
         )
      }
   };
};

/**
 * The whole feedback survey as the editor-shaped tree the save diffs against the
 * survey (`builderApi/diffSurvey`): a single section holding every question.
 *
 * `sectionId` is the section the questions already live in, so the save writes
 * back into it; leaving it out marks the section as new. Blocks carrying saved
 * ids (`block.saved`, see `savedIdsFor`) are written as updates to the
 * questions they already are, and only the rest are created.
 */
export const toSurveyItems = (survey, { sectionId } = {}) => {
   const questions = [];

   for (const block of survey.questions) {
      const written = blockToQuestions(block, questions.length + 1);
      written.forEach((item, index) =>
         questions.push(
            // Only a block's own question — its first — is the scale.
            graftSavedIds(
               item,
               block.saved?.[index],
               sectionId,
               index === 0 ? pointKeysOf(block) : null
            )
         )
      );
   }

   const id = sectionId || uuidv4();

   return [
      {
         itemLabel: "Section",
         id,
         sectionId: id,
         ...(sectionId ? {} : { created: true }),
         surveyItem: {
            // The section description is what the editor shows as its title.
            description: survey.name,
            order: 1
         },
         innerItems: questions
      }
   ];
};

// ---------------------------------------------------------------- reading back

const labelOf = (surveyItem) => surveyItem.questionLabels?.[0]?.text ?? "";

const choicesOf = (surveyItem) =>
   (surveyItem.selectQuestionChoices ?? [])
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

const emojisOf = (choices) => choices.map((item) => item.emoji ?? "");

const textsOf = (choices) => choices.map((item) => item.choiceLabels?.[0]?.text ?? "");

const sameSequence = (a, b) => a.length === b.length && a.every((item, index) => item === b[index]);

/**
 * How a saved scale's points line up with `expected`, the catalog's worst-to-best
 * list: best-first as this builder writes them (`worstFirst: false`), worst-first
 * as it wrote them before it wrote them best-first (`worstFirst: true`), or not
 * at all (null). Both are read, so a survey saved either way is still recognised
 * and its answers still land on the right points.
 */
const directionOf = (expected, actual) => {
   if (sameSequence([...expected].reverse(), actual)) return { worstFirst: false };
   if (sameSequence(expected, actual)) return { worstFirst: true };
   return null;
};

/** The smiley set whose points match these emoji, if any, and which way round. */
const matchSmileySet = (choices) => {
   const emojis = emojisOf(choices);

   for (const [key] of Object.entries(SMILEY_SETS)) {
      for (const scale of [3, 5]) {
         const expected = getSmileyPoints(key, scale).map((point) => point.emoji);
         const direction = directionOf(expected, emojis);
         if (direction) return { set: key, scale, ...direction };
      }
   }

   return null;
};

/** The thumb icon set whose up/down pair matches these two choices, if any. */
const matchThumbSet = (choices) => {
   if (choices.length !== 2) return null;
   const emojis = emojisOf(choices);

   const match = Object.entries(THUMB_SETS).find(
      ([, set]) => set.up === emojis[0] && set.down === emojis[1]
   );
   return match ? match[0] : null;
};

const contactFieldFor = (surveyItem) => {
   const label = labelOf(surveyItem).toLowerCase();
   return CONTACT_FIELDS.find((field) => field.label.toLowerCase() === label) ?? null;
};

/** The Likert scale whose labels match these choices exactly, if any, and which way round. */
const matchLikertScale = (texts) => {
   for (const scale of [5, 3]) {
      const direction = directionOf(getLikertLabels(scale), texts);
      if (direction) return { scale, ...direction };
   }
   return null;
};

/**
 * True when a saved smiley or agree/disagree question lists its points worst to
 * best — one saved before these were written best-first, and not saved since.
 */
const savedWorstFirst = (type, choices) => {
   if (type === BlockType.SMILEY) return Boolean(matchSmileySet(choices)?.worstFirst);
   if (type === BlockType.LIKERT) return Boolean(matchLikertScale(textsOf(choices))?.worstFirst);
   return false;
};

/**
 * A saved question's choices in the order its block writes them — the order every
 * position in this module counts in: `chosenOrders`, a block's `choiceIds`, the
 * choice a skip-logic condition names.
 *
 * That is the saved order, except on a smiley or agree/disagree question still
 * saved worst-first, which is turned round so each position lands on its own
 * point. Its ids then go back onto those same points in the new order
 * (`graftSavedIds`), which is how such a survey moves to best-first on its next
 * save without moving an answer already filed against it.
 */
const choicesAsWritten = (type, surveyItem) => {
   const choices = choicesOf(surveyItem);
   return savedWorstFirst(type, choices) ? [...choices].reverse() : choices;
};

/**
 * Which point each choice of a saved smiley or agree/disagree question is, in
 * the order `choicesAsWritten` lists them — the same keys `pointKeysOf` gives
 * the block, read off the saved question's own scale. Null when the question is
 * not recognised as one, which leaves its choices matched by position.
 */
const savedPointKeys = (type, surveyItem) => {
   const choices = choicesOf(surveyItem);

   if (type === BlockType.SMILEY) {
      const smiley = matchSmileySet(choices);
      return smiley ? pointKeysOf({ type, config: { set: smiley.set, scale: smiley.scale } }) : null;
   }
   if (type === BlockType.LIKERT) {
      const likert = matchLikertScale(textsOf(choices));
      return likert ? pointKeysOf({ type, config: { scale: likert.scale } }) : null;
   }
   return null;
};

/**
 * What a block records about one question it IS on the server: the ids the next
 * save writes back into. A block holds one of these per question it writes, in
 * that order, as `block.saved`.
 *
 * `name`, `choiceNames` and `relevant` are the skip-logic side of the same
 * record. A `relevant` expression refers to a question and a choice by the
 * unique `name` the backend generates for each, so a rule cannot be written or
 * read without them; they ride here rather than on the block itself because
 * they are, like the ids, facts about the saved question rather than the
 * author's layout. Only `id`, `sectionId` and `choiceIds` are ever written
 * back (see `graftSavedIds`).
 *
 * `type` is the block the question is read as. The choices are listed in the
 * order that block writes them (`choicesAsWritten`), which is what lets the nth
 * id go back onto the nth choice written — or, on a smiley or agree/disagree
 * question, onto the choice that is the same point (`pointKeys`).
 */
const savedOf = (surveyItem, type) => {
   const choices = choicesAsWritten(type, surveyItem);

   return {
      id: surveyItem.id,
      sectionId: surveyItem.sectionId,
      choiceIds: choices.map((item) => item.id),
      pointKeys: savedPointKeys(type, surveyItem),
      name: surveyItem.name ?? "",
      choiceNames: choices.map((item) => item.name ?? ""),
      relevant: surveyItem.relevant ?? ""
   };
};

/**
 * The sub-text a persisted question carries: its hint, which is where this
 * builder writes it (`questionItem`) and where the form builder and generation
 * keep it too. The `description` field is not read — nothing writes it any more.
 */
const descriptionOf = (surveyItem) => surveyItem.surveyQuestionHints?.[0]?.text ?? "";

/** Rebuilds one block from a persisted question. */
const questionToBlock = (surveyItem) => {
   const block = readBlock(surveyItem);
   // One question read back is one block writing one question, so its saved
   // list holds exactly that one. The blocks whose extra question is really a
   // setting are merged afterwards, and their saved lists with them (see
   // feedbackLogic.attachFollowUps).
   return { ...block, saved: [savedOf(surveyItem, block.type)] };
};

/** The block a persisted question reads as, without the ids it is saved under. */
const readBlock = (surveyItem) => {
   const id = uuidv4();
   const label = labelOf(surveyItem);
   const description = descriptionOf(surveyItem);
   const required = Boolean(surveyItem.isRequired);
   const choices = choicesOf(surveyItem);
   const texts = textsOf(choices);
   const base = { id, label, description, required, options: [] };

   if (surveyItem.questionType === InputType.SELECT_MULTIPLE) {
      return {
         ...base,
         type: BlockType.TAGS,
         options: texts.map(makeOption),
         config: { allowOther: false, shuffle: false }
      };
   }

   if (surveyItem.questionType === InputType.PHONE_NUMBER) {
      return { ...base, type: BlockType.CONTACT, config: { fields: ["phone"] } };
   }

   if (surveyItem.questionType === InputType.NUMBER) {
      // A question authored elsewhere could be asking for anything — an age, a
      // room number — so an adopted one gets a wide range rather than this
      // block's tap-friendly default, which would cap the answer.
      return { ...base, type: BlockType.NUMBER, config: { min: 0, max: 999 } };
   }

   if (surveyItem.questionType === InputType.DATE) {
      return { ...base, type: BlockType.DATE, config: {} };
   }

   if (surveyItem.questionType === InputType.TEXT) {
      const field = contactFieldFor(surveyItem);
      if (field) {
         return { ...base, type: BlockType.CONTACT, config: { fields: [field.key] } };
      }

      return {
         ...base,
         type: BlockType.COMMENT,
         config: { placeholder: "Tell us in your own words…", maxLength: 300 }
      };
   }

   // Everything else arrives as SELECT_ONE; the choices say which block it was.
   const emojis = emojisOf(choices);

   if (choices.length > 0 && emojis.every((emoji) => emoji === STAR_EMOJI)) {
      return { ...base, type: BlockType.STARS, config: { max: choices.length } };
   }

   const thumbSet = matchThumbSet(choices);
   if (thumbSet) {
      return {
         ...base,
         type: BlockType.THUMBS,
         config: { iconSet: thumbSet, upText: texts[0], downText: texts[1] }
      };
   }

   // Recognised by its faces alone, so wording the author typed under them
   // comes back as their wording rather than turning the block into a choice.
   const smiley = matchSmileySet(choices);
   if (smiley) {
      const { set, scale, worstFirst } = smiley;
      return {
         ...base,
         type: BlockType.SMILEY,
         config: {
            set,
            scale,
            showLabels: true,
            labels: smileyLabelsFrom(set, scale, worstFirst ? texts : [...texts].reverse())
         }
      };
   }

   if (texts.length === 11 && texts.every((text, index) => text === String(index))) {
      return {
         ...base,
         type: BlockType.NPS,
         config: { lowLabel: "Not likely", highLabel: "Very likely" }
      };
   }

   const likert = matchLikertScale(texts);
   if (likert) {
      return { ...base, type: BlockType.LIKERT, config: { scale: likert.scale } };
   }

   // Choices counting "1".."N" are a numeric scale. The end labels are purely
   // presentational and never persisted, so they come back blank.
   if (texts.length >= 3 && texts.every((text, index) => text === String(index + 1))) {
      return {
         ...base,
         type: BlockType.SCALE,
         config: { max: texts.length, lowLabel: "", highLabel: "" }
      };
   }

   return {
      ...base,
      type: BlockType.CHOICE,
      options: texts.map(makeOption),
      config: { allowOther: false, shuffle: false }
   };
};

/** Every question in a fetched survey, in display order. */
export const surveyQuestions = (surveyItems = []) => flattenQuestions(surveyItems);

const flattenQuestions = (surveyItems = []) => {
   const questions = [];

   const walk = (items) => {
      for (const item of [...items].sort(
         (a, b) => (a.surveyItem?.order ?? 0) - (b.surveyItem?.order ?? 0)
      )) {
         if (item.itemLabel === "Section") {
            walk(item.innerItems ?? []);
         } else if (item.surveyItem) {
            questions.push(item.surveyItem);
         }
      }
   };

   walk(surveyItems);
   return questions;
};

/**
 * Per-question settings this builder has no UI for, and which `questionItem`
 * therefore writes as null/empty. A question carrying any of them must not be
 * adopted: saving would rewrite it without them.
 */
const hasUnrepresentableSettings = (surveyItem) =>
   Boolean(
      surveyItem.constraint ||
         surveyItem.calculation ||
         surveyItem.range ||
         surveyItem.maxChoices ||
         surveyItem.secondChoices?.length
   );

/**
 * True when every question in a fetched survey is one the kiosk can put on
 * screen — its type has a control in this mode.
 *
 * Deliberately weaker than `canAdopt`: showing a question is not saving it, so a
 * constrained number still renders (without that constraint), and only a type
 * with no control at all — matrix, GPS, ranking — is refused.
 */
export const canRender = (surveyItems) =>
   flattenQuestions(surveyItems).every((surveyItem) =>
      SUPPORTED_QUESTION_TYPES.includes(surveyItem.questionType)
   );

/**
 * True when every question in a fetched survey is one this builder can
 * represent — renderable, and carrying nothing this builder cannot hold. A
 * survey with anything else (a constrained number, a second choice list) must be
 * edited in the generic form builder: editing it here would drop what it carries
 * the moment that question is written back.
 *
 * A question's hint is NOT such a setting: it is the same sub-text this builder
 * keeps as a block description, so it is read into one and written back out
 * (`descriptionOf`, `questionItem`) rather than being a reason to refuse the
 * survey. This is what lets a generated survey — generation drafts sub-text as
 * a hint — be edited here at all.
 */
export const canAdopt = (surveyItems) =>
   canRender(surveyItems) &&
   flattenQuestions(surveyItems).every(
      (surveyItem) => !hasUnrepresentableSettings(surveyItem)
   );

/** Rebuilds the block list from a fetched survey. */
export const fromSurveyItems = (surveyItems) => flattenQuestions(surveyItems).map(questionToBlock);

/**
 * The choices one block writes, as `{ order, label }`.
 *
 * Derived by running the writer itself, so a skip-logic rule can only ever name
 * a scale point or option row the survey was actually written with — the same
 * reason `expectedShapes` runs it rather than counting separately.
 */
export const sourceChoiceLabels = (block) => {
   const own = blockToQuestions(block, 1)[0];

   return (own?.surveyItem?.selectQuestionChoices ?? []).map((item, index) => ({
      order: index + 1,
      label: item.choiceLabels?.[0]?.text ?? ""
   }));
};

/** How many questions a block writes — one for most, several for the composites. */
export const questionsWrittenBy = (block) => blockToQuestions(block, 1).length;

/** Every top-level section id in a fetched survey. */
const findSectionIds = (surveyItems = []) =>
   surveyItems.filter((item) => item.itemLabel === "Section").map((item) => item.id);

// -------------------------------------------------------------- lining up

/**
 * The shape of every question this layout would be written as, in order:
 * its type and how many choices it carries.
 *
 * Derived by running the writer itself, so there is one definition of what a
 * block becomes rather than a second count that can drift from it.
 */
const expectedShapes = (survey) => {
   const shapes = [];

   for (const block of survey.questions) {
      for (const item of blockToQuestions(block, shapes.length + 1)) {
         shapes.push({
            questionType: item.surveyItem.questionType,
            choiceCount: item.surveyItem.selectQuestionChoices.length
         });
      }
   }

   return shapes;
};

/**
 * True when the layout lines up with the survey question for question — the
 * same number of them, and at every position the same type and choice count.
 *
 * The count alone proves nothing: reordering blocks, shortening a scale,
 * deleting one option or switching a follow-up's style all keep the total
 * identical while moving what every position means. Checking each question
 * catches those, which is what lets the callers treat position as identity.
 */
const linesUp = (survey, questions) => {
   const shapes = expectedShapes(survey);

   return (
      questions.length === shapes.length &&
      questions.every(
         (surveyItem, index) =>
            surveyItem.questionType === shapes[index].questionType &&
            choicesOf(surveyItem).length === shapes[index].choiceCount
      )
   );
};

// --------------------------------------------------------- saving in place

/**
 * The ids every block's questions now hold on the server — one `savedOf`-shaped
 * list per block, in block order — read straight back off the survey.
 *
 * Position is the only thing tying a local block to a saved question, so this
 * is only sound while the two line up: right after a save, or on a layout that
 * has not been edited since one. It returns `null` when they don't, and callers
 * record nothing rather than ids belonging to some other question.
 */
export const savedIdsFor = (survey, surveyItems = []) => {
   const questions = flattenQuestions(surveyItems);
   if (!linesUp(survey, questions)) return null;

   const perBlock = [];
   let cursor = 0;

   for (const block of survey.questions) {
      const written = blockToQuestions(block, cursor + 1);
      const start = cursor;
      // Only a block's own question — its first — is the scale; what rides
      // behind it is a follow-up or a write-in, read as saved.
      perBlock.push(
         written.map((_, index) =>
            savedOf(questions[start + index], index === 0 ? block.type : null)
         )
      );
      cursor += written.length;
   }

   return perBlock;
};

/** Every id a block claims as a question it already is. */
const claimedIds = (survey) =>
   new Set(
      (survey.questions ?? []).flatMap((block) =>
         (block.saved ?? []).map((saved) => saved?.id).filter(Boolean)
      )
   );

/**
 * True when the survey holds questions but not one block says which of them it
 * is — a layout saved before ids were tracked, or one that drifted too far for
 * `savedIdsFor` to recover them. A save cannot tell such a layout's blocks apart
 * from brand-new questions, so it would write a second copy of every one.
 */
export const isUntracked = (survey, surveyItems = []) =>
   (survey.questions ?? []).length > 0 &&
   flattenQuestions(surveyItems).length > 0 &&
   claimedIds(survey).size === 0;

/**
 * The section a save writes into: the one the questions already live in.
 *
 * Taken from the blocks themselves so it survives a survey that picked up a
 * second section somewhere, falling back to the first top-level one for a
 * layout saved before ids were tracked. Undefined means there is nothing to
 * write back into and a fresh section is created.
 */
export const sectionToWrite = (survey, surveyItems = []) => {
   const fromBlocks = (survey.questions ?? [])
      .flatMap((block) => block.saved ?? [])
      .map((saved) => saved?.sectionId)
      .find(Boolean);

   return fromBlocks ?? findSectionIds(surveyItems)[0];
};

/**
 * The saved questions no block claims any more — the ones the author deleted.
 * Only these are removed, so every other question keeps its id and its answers.
 *
 * A layout holding blocks but no recorded ids at all predates them (see
 * `savedIdsFor`): which saved question is which is unknown, so which to delete
 * is too. `replaceUntracked` says what to do about that — set while the survey
 * has collected nothing, where clearing the old questions is the clean rewrite
 * it always was, and clear once it has, where an orphaned question on the survey
 * is recoverable and a deleted one with its submissions is not.
 *
 * An empty layout is never that case: the author removed every block, and the
 * saved questions go with them.
 */
export const droppedQuestionIds = (survey, surveyItems = [], { replaceUntracked } = {}) => {
   if (!replaceUntracked && isUntracked(survey, surveyItems)) return [];

   const claimed = claimedIds(survey);
   return flattenQuestions(surveyItems)
      .map((surveyItem) => surveyItem.id)
      .filter((questionId) => questionId && !claimed.has(questionId));
};

/**
 * Why a layout and a survey don't line up, as a clause for the message shown to
 * whoever hit it — a count, or the block sitting where a different question is
 * saved. `null` when they do line up.
 *
 * The generic "no longer matches" wording leaves an author guessing at which of
 * several very different causes they are looking at: unsaved edits, a survey
 * changed in the standard builder, or questions written twice.
 */
export const describeDrift = (survey, surveyItems = []) => {
   const questions = flattenQuestions(surveyItems);
   const shapes = expectedShapes(survey);

   if (questions.length !== shapes.length) {
      return `the survey has ${questions.length} saved ${
         questions.length === 1 ? "question" : "questions"
      } and this layout describes ${shapes.length}`;
   }

   const at = questions.findIndex(
      (surveyItem, index) =>
         surveyItem.questionType !== shapes[index].questionType ||
         choicesOf(surveyItem).length !== shapes[index].choiceCount
   );
   if (at === -1) return null;

   // Which block it is, by the label its author typed.
   let cursor = 0;
   for (const block of survey.questions) {
      const written = blockToQuestions(block, cursor + 1).length;
      if (at < cursor + written) {
         const label = block.label?.trim() || "Untitled question";
         return `“${label}” no longer matches the question saved here (question ${at + 1})`;
      }
      cursor += written;
   }

   return `question ${at + 1} no longer matches the one saved in its place`;
};

// ----------------------------------------------------------------- submitting

/** How many points a smiley or agree/disagree block's scale has. */
const pointCount = (block) =>
   block.type === BlockType.SMILEY
      ? getSmileyPoints(block.config.set, block.config.scale).length
      : getLikertLabels(block.config.scale).length;

/**
 * The choices an answer selected, as the 1-based `order` they were written
 * with. Empty for an unanswered question.
 *
 * Every scale block stores its points as ordinary choices in a fixed order, so
 * the answer a kiosk holds — a smiley index, a star count, an NPS score — is
 * really a position in that list. This is where that is spelled out; it mirrors
 * `blockToQuestions` above, which wrote them in exactly this order.
 */
export const chosenOrders = (block, value) => {
   if (value === null || value === undefined || value === "") return [];

   switch (block.type) {
      case BlockType.SMILEY:
      case BlockType.LIKERT:
         // 0-based from the worst point, written best-first — so the best point,
         // the highest value, is the first choice.
         return [pointCount(block) - value];
      case BlockType.STARS:
         // 1..max, already the choice's order.
         return [value];
      case BlockType.NPS:
         // 0..10, written as eleven choices labelled "0".."10".
         return [value + 1];
      case BlockType.SCALE:
         // 1..max, already the choice's order.
         return [value];
      case BlockType.THUMBS:
         return [value === "up" ? 1 : 2];
      case BlockType.CHOICE:
      case BlockType.TAGS: {
         const selected = block.type === BlockType.TAGS ? value : [value];
         // The same blank-free list the choices were written from; the "Other"
         // option was written directly behind it.
         const options = selectableOptions(block);
         return (Array.isArray(selected) ? selected : [])
            .map((optionId) =>
               optionId === OTHER_OPTION_ID
                  ? (otherEnabled(block) ? options.length + 1 : 0)
                  : options.findIndex((item) => item.id === optionId) + 1
            )
            .filter((order) => order > 0);
      }
      default:
         return [];
   }
};

/** The answer a 1-based choice position stands for — the inverse of `chosenOrders`. */
const valueAtOrder = (block, order) => {
   switch (block.type) {
      case BlockType.SMILEY:
      case BlockType.LIKERT:
         // Written best-first, so the last choice is the answer `0`.
         return pointCount(block) - order;
      case BlockType.NPS:
         // 0-based, so the first choice is the answer `0`.
         return order - 1;
      case BlockType.STARS:
      case BlockType.SCALE:
         return order;
      case BlockType.THUMBS:
         return order === 1 ? "up" : "down";
      default:
         return null;
   }
};

/**
 * The choice positions a low rating lands on — the unhappy half `isLowScore`
 * names, in the terms the saved question holds.
 *
 * Read by asking `isLowScore` about each position rather than restating the
 * midpoint arithmetic, so the gate written onto the follow-up question
 * (`followUpRelevant`) and the box the kiosk reveals can never disagree about
 * which ratings count as low.
 */
export const lowScoreOrders = (block) =>
   sourceChoiceLabels(block)
      .map(({ order }) => order)
      .filter((order) => isLowScore(block, valueAtOrder(block, order)));

/** A typed answer for a free-text question — phone numbers ride their own field. */
const textAnswer = (surveyItem, text) =>
   surveyItem.questionType === InputType.PHONE_NUMBER ? { number: text } : { text };

/**
 * One kiosk run, resolved against the survey as the server holds it.
 *
 * Nothing in the local layout can be sent as-is: its question and option ids are
 * client-side uuids, while a submission has to name the server's. They are
 * recovered by walking the blocks and the survey's questions in the same order
 * `toSurveyItems` wrote them — one question per block, except CONTACT and
 * MULTISTARS (one per requested detail / aspect) and blocks with a low-score
 * follow-up or an "Other…" write-in, whose extra question rides directly
 * behind their own.
 *
 * Returns `null` when the two no longer line up, which means the layout was
 * edited without saving or the survey was restructured elsewhere. Guessing past
 * that would file answers against the wrong questions, so callers must treat
 * null as "not submittable" rather than send what they have.
 *
 * `hidden` names the blocks a skip-logic rule kept off screen. They are filed as
 * though they were not on the survey at all: no answer, and their question ids
 * left out of the list of what was asked — the respondent never saw them, and a
 * blank answer to a question that was never put to someone is not the same fact
 * as one they chose to skip.
 */
export const toSubmission = (survey, surveyItems, answers, { hidden = [] } = {}) => {
   const questions = flattenQuestions(surveyItems);

   // A drifted layout is refused rather than filing a guest's answers against
   // the wrong questions and choices.
   if (questions.length === 0 || !linesUp(survey, questions)) return null;

   const skipped = new Set(hidden);
   const asked = [];
   const resolved = [];
   let cursor = 0;

   for (const block of survey.questions) {
      const value = answers?.[block.id];

      // A block a rule hid still occupies its questions on the survey, so the
      // cursor has to step over every one of them — the positions after it mean
      // nothing otherwise.
      const span = blockToQuestions(block, cursor + 1).length;
      if (skipped.has(block.id)) {
         cursor += span;
         continue;
      }
      // The follow-up question is gated on the survey itself (see
      // feedbackLogic.followUpRelevant), so a rating that did not open the box
      // means the guest was never asked it — reported the way a rule-hidden
      // block is, by leaving it out of what was rendered rather than filing a
      // blank answer to a question nobody saw. It is the last question the
      // block writes.
      const askedIds = questions.slice(cursor, cursor + span).map((surveyItem) => surveyItem.id);
      if (followUpOf(block) && !isLowScore(block, value)) askedIds.pop();
      asked.push(...askedIds);

      if (block.type === BlockType.CONTACT) {
         for (const field of contactFieldsOf(block)) {
            const surveyItem = questions[cursor++];
            const text = String(value?.[field.key] ?? "").trim();
            if (text) resolved.push({ surveyItem, answerDetail: textAnswer(surveyItem, text) });
         }
         continue;
      }

      if (block.type === BlockType.MULTISTARS) {
         // One star question per aspect, each holding choices 1..5.
         for (const aspect of block.options ?? []) {
            const surveyItem = questions[cursor++];
            const stars = value?.[aspect.id];
            if (typeof stars !== "number") continue;

            const starChoice = choicesOf(surveyItem)[stars - 1]?.id;
            if (starChoice) resolved.push({ surveyItem, answerDetail: { choices: [starChoice] } });
         }
         continue;
      }

      const surveyItem = questions[cursor++];

      if (block.type === BlockType.COMMENT || block.type === BlockType.SHORT_TEXT) {
         const text = String(value ?? "").trim();
         if (text) resolved.push({ surveyItem, answerDetail: textAnswer(surveyItem, text) });
         continue;
      }

      if (block.type === BlockType.NUMBER) {
         if (typeof value === "number") {
            resolved.push({ surveyItem, answerDetail: { number: value } });
         }
         continue;
      }

      if (block.type === BlockType.DATE) {
         // Held as the "YYYY-MM-DD" string the pickers exchange — the same
         // `answerDetail.text` the generic Date submission type files.
         const text = String(value ?? "").trim();
         if (text) resolved.push({ surveyItem, answerDetail: { text } });
         continue;
      }

      // The choices as written: order 1..N, so the order is the position. A
      // scale still saved worst-first is read the right way round.
      const choices = choicesAsWritten(block.type, surveyItem);
      const chosen = chosenOrders(block, value)
         .map((order) => choices[order - 1]?.id)
         .filter(Boolean);

      if (chosen.length) resolved.push({ surveyItem, answerDetail: { choices: chosen } });

      // The "Other…" write-in question rides directly behind its block's own.
      if (otherEnabled(block)) {
         const otherItem = questions[cursor++];
         const text = String(answers?.[otherKey(block.id)] ?? "").trim();
         // Filed only while "Other" is among the submitted choices; text typed
         // before the option was deselected is dropped along with it.
         if (text && otherSelected(block, value)) {
            resolved.push({ surveyItem: otherItem, answerDetail: textAnswer(otherItem, text) });
         }
      }

      // The follow-up question rides directly behind its block's own.
      const followUp = followUpOf(block);
      if (followUp) {
         const followUpItem = questions[cursor++];
         // Filed only when the rating being submitted would have shown the box;
         // anything given before the rating was raised is dropped with it.
         if (isLowScore(block, value)) {
            if (followUpStyleOf(followUp) === "reasons") {
               const reasons = followUpReasonsOf(followUp);
               const followUpChoices = choicesOf(followUpItem);
               const picked = answers?.[followUpKey(block.id)];
               const chosenReasons = (Array.isArray(picked) ? picked : [])
                  .map((reasonId) => reasons.findIndex((reason) => reason.id === reasonId))
                  .filter((index) => index >= 0)
                  .map((index) => followUpChoices[index]?.id)
                  .filter(Boolean);
               if (chosenReasons.length) {
                  resolved.push({
                     surveyItem: followUpItem,
                     answerDetail: { choices: chosenReasons }
                  });
               }
            } else {
               const text = String(answers?.[followUpKey(block.id)] ?? "").trim();
               if (text) {
                  resolved.push({
                     surveyItem: followUpItem,
                     answerDetail: textAnswer(followUpItem, text)
                  });
               }
            }
         }
      }
   }

   // Every question the respondent was shown, answered or not — the endpoint
   // takes the full list alongside the answers, as the generic viewer sends it
   // (`renderedElementIds`). A question a rule hid was never rendered, so it is
   // not in this list either.
   return { answers: resolved, questionIds: asked };
};
